import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useYorumlar, type YorumPuanFiltresi } from "@/api/yorumlar";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui";
import { AiAnalizModal } from "@/features/yorumlar/AiAnalizModal";
import { analyzeYorumlar } from "@/features/yorumlar/aiAnaliz";
import { YildizFiltre } from "@/features/yorumlar/YildizFiltre";
import { YorumKart } from "@/features/yorumlar/YorumKart";
import { t } from "@/i18n";
import { fmtInt } from "@/lib/utils";

/** Liste bu kadar kart ile baslar, "Daha fazla göster" ile katman katman acilir (gorev C: eskiden 244 kart tek seferde render ediliyordu). */
const SAYFA_BOYU = 20;

/** /yorumlar — yildiz filtresi + AI analiz (docs/arayuz-tasarim-rehberi.md). */
export function Yorumlar() {
  const [rating, setRating] = useState<YorumPuanFiltresi>("hepsi");
  const [urun, setUrun] = useState("");
  const [arama, setArama] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [gosterilenSayi, setGosterilenSayi] = useState(SAYFA_BOYU);
  const yorumlarQuery = useYorumlar(rating);

  // Eski arayuz (legacy/js/yorumlar.js fillUrunFiltre + arama kutusu): urun
  // bazli acilir liste ve serbest metin arama — istemci tarafinda uygulanir.
  const veri = yorumlarQuery.data;
  const tumYorumlar = useMemo(() => (veri && veri.status === "ok" ? veri.yorumlar : []), [veri]);
  const urunler = useMemo(
    () => Array.from(new Set(tumYorumlar.map((y) => y.product_name).filter((n): n is string => Boolean(n)))).sort((a, b) => a.localeCompare(b, "tr")),
    [tumYorumlar],
  );
  const gorunenYorumlar = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr");
    return tumYorumlar.filter((y) => {
      if (urun && y.product_name !== urun) return false;
      if (!q) return true;
      const metin = `${y.body ?? ""} ${y.customer_name ?? ""} ${y.product_name ?? ""} ${y.sku ?? ""}`.toLocaleLowerCase("tr");
      return metin.includes(q);
    });
  }, [tumYorumlar, urun, arama]);

  // Filtre/arama/urun degisince sayfalama basa doner (gorev talimati).
  useEffect(() => {
    setGosterilenSayi(SAYFA_BOYU);
  }, [rating, urun, arama]);

  const gosterilenListe = gorunenYorumlar.slice(0, gosterilenSayi);
  const kalan = gorunenYorumlar.length - gosterilenListe.length;

  const aiMutation = useMutation({
    mutationFn: (userPrompt?: string) => {
      const data = yorumlarQuery.data;
      const ozet = data && data.status === "ok" ? data.ozet : {};
      return analyzeYorumlar(gorunenYorumlar, ozet, String(rating), userPrompt);
    },
  });

  function analizAc() {
    setModalOpen(true);
    aiMutation.mutate(undefined);
  }

  function takipSorusu(question: string) {
    aiMutation.mutate(question);
  }

  if (yorumlarQuery.isLoading) {
    return <p className="text-sm text-muted">{t("common.loading")}</p>;
  }
  if (yorumlarQuery.isError) {
    return (
      <p role="alert" className="text-sm text-red">
        {t("common.error")}
      </p>
    );
  }

  const data = yorumlarQuery.data;
  if (!data) return null;
  if (data.status === "not_supported") {
    return <EmptyState kind="not_supported" />;
  }

  const yorumlar = data.yorumlar;
  const ozet = data.ozet;

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-xl font-bold text-navy">{t("nav.yorumlar")}</h1>
        <Button variant="outline" onClick={analizAc} disabled={yorumlar.length === 0}>
          {t("yorumlar.ai.buton")}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <div className="kpi">
          <p className="kpi-lbl">{t("yorumlar.ozet.toplam")}</p>
          <p className="kpi-val mt-2 text-[22px]">{fmtInt(ozet.toplam)}</p>
        </div>
        <div className={`kpi ${(ozet.onay_bekleyen ?? 0) > 0 ? "kpi-warn" : ""}`}>
          <p className="kpi-lbl">{t("yorumlar.ozet.onay_bekleyen")}</p>
          <p className="kpi-val mt-2 text-[22px]">{fmtInt(ozet.onay_bekleyen)}</p>
        </div>
        <div className="kpi">
          <p className="kpi-lbl">{t("yorumlar.ozet.ort_puan")}</p>
          <p className="kpi-val mt-2 text-[22px]">{ozet.ort_puan != null ? `${ozet.ort_puan.toFixed(1)} ★` : "—"}</p>
        </div>
        <div className="kpi">
          <p className="kpi-lbl">{t("yorumlar.ozet.urun_sayisi")}</p>
          <p className="kpi-val mt-2 text-[22px]">{fmtInt(ozet.urun_sayisi)}</p>
        </div>
        <div className={`kpi ${(ozet.dusuk_puan ?? 0) > 0 ? "kpi-bad" : ""}`}>
          <p className="kpi-lbl">{t("yorumlar.ozet.dusuk_puan")}</p>
          <p className="kpi-val mt-2 text-[22px]">{fmtInt(ozet.dusuk_puan)}</p>
        </div>
        <div className="kpi">
          <p className="kpi-lbl">{t("yorumlar.ozet.oneren_yuzde")}</p>
          <p className="kpi-val mt-2 text-[22px]">{ozet.oneren_yuzde != null ? `%${Math.round(ozet.oneren_yuzde)}` : "—"}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <YildizFiltre value={rating} onChange={setRating} />
        <select
          aria-label={t("yorumlar.filtre.urun")}
          value={urun}
          onChange={(e) => setUrun(e.target.value)}
          className="input max-w-[220px]"
        >
          <option value="">{t("yorumlar.filtre.tum_urunler")}</option>
          {urunler.map((ad) => (
            <option key={ad} value={ad}>
              {ad}
            </option>
          ))}
        </select>
        <input
          type="search"
          aria-label={t("yorumlar.filtre.arama")}
          placeholder={t("yorumlar.filtre.arama")}
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          className="input max-w-xs"
        />
      </div>

      {/* INCELEME BULGUSU: filtre degisimi eskiden TUM sayfayi (baslik/
          istatistik/filtre dahil) "Yükleniyor…" mesajina dusuruyordu.
          `useYorumlar` artik `keepPreviousData` kullanir — sayfa iskeleti
          HER ZAMAN kalir, `isPlaceholderData` (yeni filtre icin veri henuz
          gelmedi, ekrandaki eski filtreye ait) sirasinda yalniz liste alani
          kisa bir yukleniyor durumu gosterir. */}
      {yorumlarQuery.isPlaceholderData ? (
        <p className="text-sm text-muted">{t("common.loading")}</p>
      ) : gorunenYorumlar.length === 0 ? (
        <EmptyState kind="no_data" />
      ) : (
        <>
          <div className="space-y-3">
            {gosterilenListe.map((yorum) => (
              <YorumKart key={yorum.id} yorum={yorum} />
            ))}
          </div>
          {kalan > 0 && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => setGosterilenSayi((n) => n + SAYFA_BOYU)}>
                {t("yorumlar.liste.daha_fazla", { kalan: String(kalan) })}
              </Button>
            </div>
          )}
        </>
      )}

      <AiAnalizModal
        open={modalOpen}
        isLoading={aiMutation.isPending}
        error={aiMutation.error}
        data={aiMutation.data}
        onClose={() => setModalOpen(false)}
        onFollowUp={takipSorusu}
      />
    </section>
  );
}
